import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { navigate } from 'gatsby'
import { AuthContext } from '../../../context/authProvider'

import {
  StyledAction,
  StyledGameWrapper,
  StyledInnerWrapper,
  StyledOuterWrapper,
  StyledPlay,
  StyledPlayForFun,
} from './styles'
import { ResponsiveImage } from '../responsiveImage'
import { Button } from '../button'
import { If } from '../if'
import { useTranslation } from '../../../context/translationProvider'
import { game_proptype } from '../../../propTypes'
import { isItemActive } from '../../../dataManipulation/cmsItems'
import { GameTags } from '../../molecules/gameTags'
import {
  normalizeDataLayerText,
  pushDataLayer,
} from '../../../utils/pushDataLayer'
import { isMobileApp } from '../../../utils/mobileAppFunctionality'
import { useFeatureValue } from '@growthbook/growthbook-react'
import storageService from '../../../services/storageService'
import { useEventTracking } from '../../../context/eventTrackingProvider'

const ClickableGameTile = (props) => {
  const {
    game,
    isWide,
    casinoSlug,
    loginSlug,
    forceLogin,
    hideGameTags,
    triggerGAEvent,
    screenName,
    position,
    category,
  } = props
  const {
    title,
    thumbnail,
    thumbnail_tall,
    game_tags,
    slug,
    maintenance_mode,
    disable_play_for_fun,
    providerName,
  } = game ?? {}
  const { translate } = useTranslation()

  const { isLoggedIn, setTriggerGeolocation } = useContext(AuthContext)

  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS } = useEventTracking()

  const gameUrl = `/${casinoSlug}/game/${slug}/`
  const restrictGameClickInDevelop = useFeatureValue('restrict_gameclick_in_develop_mode')
  const env = process.env.GATSBY_ENV

  const isUnderMaintenance =
    maintenance_mode?.active_from &&
    isItemActive({
      activeFrom: maintenance_mode.active_from,
      activeTo: maintenance_mode.active_to,
    })

  const getApplicableTags = () => {
    let tags = [...game_tags]
    if (isUnderMaintenance) tags?.push('gametag_maintenance')
    if (game?.coming_soon) tags = ['gametag_coming_soon']
    return tags
  }

  const applicableGameTags = getApplicableTags()

  const getWrapperProps = () => {
    if (!forceLogin || isLoggedIn) return { to: gameUrl }

    return {
      to: `/${loginSlug}`,
      state: {
        redirectURL: gameUrl,
      },
    }
  }

  const wrapperProps = getWrapperProps()

  const onGameTileClick = () => {
    if (triggerGAEvent) {
      pushDataLayer({
        eventName: 'game-opened',
        data: {
          location: screenName,
          game_id: slug,
          game_name: title,
          category_name: normalizeDataLayerText(category),
          provider_name: normalizeDataLayerText(providerName),
          position,
        },
      })
    }
  }

  const handleGameLinkClick = (gameUrl, e) => {
    if (isMobileApp() || (
      !restrictGameClickInDevelop && env !== 'prod'
    )) navigate(gameUrl)
      
    e.preventDefault()
  
    if (!isLoggedIn) {
      navigate(gameUrl)
      return
    }
  
    const locationState = storageService.getValue('geoStatus')

    if (locationState === 'granted') {
      navigate(gameUrl)
      return
    }
  
    setTriggerGeolocation(true)
  }

  const handleEnterClick = () => {
    trackEvent(EVENT_NAMES.LOGIN_STARTED, { location: EVENT_LOCATIONS.GAME_THUMBNAIL });
  }

  const id = `playForFun-${title}`
  
  return (
    <StyledOuterWrapper onClick={onGameTileClick}>
      <StyledGameWrapper name={title} onClick={(e) => handleGameLinkClick(gameUrl, e)} {...wrapperProps}>
        <StyledInnerWrapper
          isWide={isWide}
          grayOut={isUnderMaintenance || game?.coming_soon}
        >
          <ResponsiveImage
            loading="lazy"
            animateLoading
            image={
              isWide || !thumbnail_tall?.sizes?.medium
                ? thumbnail
                : thumbnail_tall
            }
          />
          <GameTags gameTags={applicableGameTags} hideGameTags={hideGameTags} />
        </StyledInnerWrapper>
      </StyledGameWrapper>
      <If
        condition={!isUnderMaintenance}
        render={() => (
          <StyledAction isLoggedIn={isLoggedIn}>
            <If
              condition={isLoggedIn}
              render={() => <StyledPlay onClick={(e) => handleGameLinkClick(gameUrl, e)}/>}
              renderElse={() => (
                <>
                  <Button
                    red
                    href={`/${loginSlug}`}
                    redirectURL={gameUrl}
                    id={`loginGame-${title}`}
                    onClick={handleEnterClick}
                  >
                    {translate('casino.login')}
                  </Button>
                  <If
                    condition={!forceLogin}
                    render={() => (
                      <StyledPlayForFun 
                        id={id} 
                        data-qa={id}
                        onClick={(e) => handleGameLinkClick(gameUrl, e)}
                      >
                        {disable_play_for_fun
                          ? translate('common.showMore')
                          : translate('casino.playForFun')}
                      </StyledPlayForFun>
                    )}
                  />
                </>
              )}
            />
          </StyledAction>
        )}
      />
    </StyledOuterWrapper>
  )
}

ClickableGameTile.defaultProps = {
  isWide: false,
  forceLogin: false,
  game: {},
  casinoSlug: '',
  loginSlug: '',
}

ClickableGameTile.propTypes = {
  isWide: PropTypes.bool,
  forceLogin: PropTypes.bool,
  game: game_proptype,
  casinoSlug: PropTypes.string,
  loginSlug: PropTypes.string,
  hideGameTags: PropTypes.bool,
  triggerGAEvent: PropTypes.bool,
  screenName: PropTypes.string,
  category: PropTypes.string,
  position: PropTypes.number,
}

export { ClickableGameTile }
